import {
  html,
  render,
  serviceAuthorizationSelectMenuButtonContent
} from '@cumu/shared';
import { loc_loadingEllipses } from '@cumu/strings';
import { controller, target } from '@github/catalyst';
import type RemoteInputElement from '@github/remote-input-element';
import { getLocale } from '../locale';
import { dispatchChange } from './util';

const locale = getLocale();

export const serviceAuthorizationSelectChangeEvent =
  'service-authorization-select-change';

@controller('service-authorization-select')
export default class ServiceAuthorizationSelectElement extends HTMLElement {
  @target declare preview: HTMLElement;
  @target declare input: HTMLInputElement;

  get remoteInput() {
    return this.querySelector<RemoteInputElement>('remote-input')!;
  }

  get organization_id() {
    return this.getAttribute('organization_id')!;
  }

  get service_authorization_id() {
    return this.input.value || null;
  }

  get name() {
    return this.getAttribute('name')!;
  }

  get required() {
    return this.hasAttribute('required');
  }

  get menuButton() {
    return this.querySelector<HTMLInputElement>('summary [data-menu-button]')!;
  }

  connectedCallback() {
    this.addEventListener('focus', this, { capture: true });
  }

  disconnectedCallback() {
    this.removeEventListener('focus', this, { capture: true });
  }

  handleEvent(event: Event) {
    switch (event.type) {
      case 'focus': {
        const remoteInput = this.remoteInput;
        const url = new URL(remoteInput.src, location.origin);
        const form = this.closest('form');
        const startDateInput = form?.elements.namedItem('start_date:date');
        if (
          startDateInput instanceof HTMLInputElement &&
          isFinite(Date.parse(startDateInput.value))
        ) {
          url.searchParams.set(
            'start_date',
            new Date(startDateInput.value).toISOString().substr(0, 10)
          );
        }
        if (remoteInput.src !== url.href) {
          remoteInput.src = url.href;
        }
        return;
      }
      default:
        throw new Error(`Unhandled event: ${event.type}`);
    }
  }

  detailsMenuSelected() {
    dispatchChange(this.input);
    this.dispatchEvent(
      new CustomEvent(serviceAuthorizationSelectChangeEvent, { bubbles: true })
    );
    const service_authorization_id = this.service_authorization_id;
    this.preview?.remove();
    if (service_authorization_id) {
      this.insertAdjacentHTML(
        'beforeend',
        render(
          html`<include-fragment
            src="/api/organizations/${
              this.organization_id
            }/service-authorizations/${
              this.service_authorization_id
            }/summary-card?target=service-authorization-select.preview"
            accept="text/fragment+html"
            class="d-flex gap-2 flex-items-center flex-justify-center py-4 color-fg-muted color-bg-inset rounded"
            data-target="service-authorization-select.preview"
            role="status"
          >
            <span class="spinner-border"></span>
            <span>${loc_loadingEllipses[locale]}</span>
          </include-fragment>`
        )
      );
    }
  }

  inputFocused() {
    this.querySelector('summary')?.click();
  }

  clear() {
    // clear value
    if (this.service_authorization_id) {
      this.menuButton.innerHTML = render(
        serviceAuthorizationSelectMenuButtonContent(
          locale,
          this.name,
          this.required
        )
      );
      dispatchChange(this.input);
      this.dispatchEvent(
        new CustomEvent(serviceAuthorizationSelectChangeEvent, {
          bubbles: true
        })
      );
    }

    // clear preview
    this.preview?.remove();

    // clear menu filter and results
    const filterInput = this.remoteInput.input!;
    filterInput.value = '';
    dispatchChange(filterInput);
    this.querySelector('SelectMenu-list')!.innerHTML = '';
  }
}

declare global {
  interface Window {
    ServiceAuthorizationSelectElement: typeof ServiceAuthorizationSelectElement;
  }
  interface HTMLElementTagNameMap {
    'service-authorization-select': ServiceAuthorizationSelectElement;
  }
}
